import { Box, IconButton, Button, Typography, useTheme, TextField, MenuItem, Select, FormControl, InputLabel, Paper, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, Fab } from "@mui/material";
import { useEffect, useState } from "react";
import { useAuth } from "../../AuthContext";
import { tokens } from "../../theme";
import { ToastContainer, toast } from "react-toastify";
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import 'react-toastify/dist/ReactToastify.css';
import Nodata from '../../assets/errors/nodata.png';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const CashbookView = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totals, setTotals] = useState({ income: 0, expense: 0, balance: 0 });
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    transaction_date: new Date().toISOString().split('T')[0],
    description: '',
    category: '',
    type: 'income',
    amount: '',
    payment_method: 'cash',
    reference_number: '',
    status: 'completed'
  });
  
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData } = useAuth();

  // Fetch cashbook data
  useEffect(() => {
    fetchCashbookData();
  }, []);

  const fetchCashbookData = async () => {
    try {
      setLoading(true);
      const response = await getData('proccess.php', 'cashbook');
      if (response.success) {
        setTransactions(Array.isArray(response.data) ? response.data : []);
        setFilteredTransactions(Array.isArray(response.data) ? response.data : []);
        calculateTotals(Array.isArray(response.data) ? response.data : []);
      } else {
        toast.error(response.message || "Failed to load cashbook data");
      }
    } catch (err) {
      toast.error("Network error: Failed to fetch cashbook data");
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const calculateTotals = (data) => {
    const income = data
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
    const expense = data
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
    
    setTotals({
      income: income,
      expense: expense,
      balance: income - expense
    });
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...transactions];

    if (startDate && endDate) {
      filtered = filtered.filter(t => 
        t.transaction_date >= startDate && t.transaction_date <= endDate
      );
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(t => t.category === categoryFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(t => 
        t.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.reference_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredTransactions(filtered);
    calculateTotals(filtered);
    setCurrentPage(1);
  }, [transactions, startDate, endDate, categoryFilter, searchTerm]);

  // Separate income and expense transactions
  const incomeTransactions = filteredTransactions.filter(t => t.type === 'income');
  const expenseTransactions = filteredTransactions.filter(t => t.type === 'expense');

  // Pagination for both sides
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentIncome = incomeTransactions.slice(indexOfFirstEntry, indexOfLastEntry);
  const currentExpense = expenseTransactions.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.max(
    Math.ceil(incomeTransactions.length / entriesPerPage),
    Math.ceil(expenseTransactions.length / entriesPerPage)
  );

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleEntriesPerPageChange = (event) => {
    setEntriesPerPage(parseInt(event.target.value));
    setCurrentPage(1);
  };

  // Reset filters
  const resetFilters = () => {
    setStartDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
    setEndDate(new Date().toISOString().split('T')[0]);
    setCategoryFilter('all');
    setSearchTerm('');
  };

  // Form handlers
  const handleOpenAddDialog = () => {
    setDialogMode('add');
    setFormData({
      transaction_date: new Date().toISOString().split('T')[0],
      description: '',
      category: '',
      type: 'income',
      amount: '',
      payment_method: 'cash',
      reference_number: '',
      status: 'completed'
    });
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (transaction) => {
    setDialogMode('edit');
    setSelectedTransaction(transaction);
    setFormData({
      transaction_date: transaction.transaction_date,
      description: transaction.description,
      category: transaction.category,
      type: transaction.type,
      amount: transaction.amount,
      payment_method: transaction.payment_method || 'cash',
      reference_number: transaction.reference_number || '',
      status: transaction.status || 'completed'
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTransaction(null);
    setFormData({
      transaction_date: new Date().toISOString().split('T')[0],
      description: '',
      category: '',
      type: 'income',
      amount: '',
      payment_method: 'cash',
      reference_number: '',
      status: 'completed'
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.description) {
      toast.error("Please enter a description");
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (!formData.category) {
      toast.error("Please select a category");
      return;
    }

    try {
      let response;
      if (dialogMode === 'add') {
        response = await addData('proccess.php', 'cashbook', formData);
        if (response.success) {
          toast.success("Transaction added successfully");
          fetchCashbookData();
        } else {
          toast.error(response.message || "Failed to add transaction");
        }
      } else {
        response = await updateData('proccess.php', 'cashbook', formData, selectedTransaction.id);
        if (response.success) {
          toast.success("Transaction updated successfully");
          fetchCashbookData();
        } else {
          toast.error(response.message || "Failed to update transaction");
        }
      }
      handleCloseDialog();
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
  };

  const handleDeleteClick = (transaction) => {
    setTransactionToDelete(transaction);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteData('proccess.php', 'cashbook', transactionToDelete.id);
      if (response.success) {
        toast.success("Transaction deleted successfully");
        fetchCashbookData();
      } else {
        toast.error(response.message || "Failed to delete transaction");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete transaction");
      console.error("Delete error:", err);
    }
    setDeleteConfirmOpen(false);
    setTransactionToDelete(null);
  };

  // Export to Excel
  const exportToExcel = () => {
    if (filteredTransactions.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const exportData = filteredTransactions.map(t => ({
        'Date': t.transaction_date,
        'Description': t.description,
        'Category': t.category,
        'Type': t.type === 'income' ? 'Income' : 'Expense',
        'Amount (N$)': parseFloat(t.amount).toFixed(2),
        'Payment Method': t.payment_method,
        'Reference Number': t.reference_number || '',
        'Status': t.status === 'completed' ? 'Completed' : 'Pending',
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Cashbook');
      XLSX.writeFile(wb, `cashbook_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      toast.success("Cashbook data exported successfully");
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export data");
    }
  };

  // Export to PDF
  const exportToPDF = async () => {
    if (filteredTransactions.length === 0) {
      toast.warning("No data to export");
      return;
    }

    const element = document.getElementById('cashbook-content');
    if (!element) return;

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        logging: false,
        useCORS: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const imgWidth = 280;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 10, 10, imgWidth, imgHeight);
      pdf.save(`cashbook_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success("PDF exported successfully");
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error("Failed to export PDF");
    }
  };

  const uniqueCategories = ['all', ...new Set(transactions.map(t => t.category).filter(Boolean))];
  const formatCurrency = (amount) => {
    return `N$${parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Category options
  const incomeCategories = ['Sales', 'Services', 'Donations', 'Grants', 'Interest', 'Investment', 'Other Income'];
  const expenseCategories = ['Salaries', 'Rent', 'Utilities', 'Supplies', 'Equipment', 'Travel', 'Training', 'Maintenance', 'Marketing', 'Other Expense'];

  return (
    <Box sx={{ p: 2, backgroundColor: colors.primary[50], minHeight: '100vh' }}>
      <ToastContainer position="top-right" autoClose={5000} />
      
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color={colors.dark[400]}>
            Cashbook Management
          </Typography>
          <Typography variant="body2" color={colors.dark[200]}>
            Double-Entry Cashbook Layout - Income on Left, Expenses on Right
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            sx={{ bgcolor: colors.blue[400], '&:hover': { bgcolor: colors.blue[500] } }}
          >
            Add Transaction
          </Button>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={exportToPDF}
            sx={{ borderColor: colors.red[400], color: colors.red[400] }}
          >
            Export PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportToExcel}
            sx={{ borderColor: colors.green[400], color: colors.green[400] }}
          >
            Export Excel
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: '200px', p: 2, bgcolor: colors.green[50], borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TrendingUpIcon sx={{ color: colors.green[400] }} />
            <Typography variant="body2" color={colors.dark[200]}>Total Income</Typography>
          </Box>
          <Typography variant="h5" fontWeight="bold" color={colors.green[400]}>
            {formatCurrency(totals.income)}
          </Typography>
        </Box>
        
        <Box sx={{ flex: 1, minWidth: '200px', p: 2, bgcolor: colors.red[50], borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TrendingDownIcon sx={{ color: colors.red[400] }} />
            <Typography variant="body2" color={colors.dark[200]}>Total Expense</Typography>
          </Box>
          <Typography variant="h5" fontWeight="bold" color={colors.red[400]}>
            {formatCurrency(totals.expense)}
          </Typography>
        </Box>
        
        <Box sx={{ flex: 1, minWidth: '200px', p: 2, bgcolor: colors.blue[50], borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AccountBalanceIcon sx={{ color: colors.blue[400] }} />
            <Typography variant="body2" color={colors.dark[200]}>Net Balance</Typography>
          </Box>
          <Typography variant="h5" fontWeight="bold" color={colors.blue[400]}>
            {formatCurrency(totals.balance)}
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filters</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select value={categoryFilter} label="Category" onChange={(e) => setCategoryFilter(e.target.value)}>
              <MenuItem value="all">All Categories</MenuItem>
              {uniqueCategories.filter(c => c !== 'all').map(category => (
                <MenuItem key={category} value={category}>{category}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<ClearIcon />} onClick={resetFilters}>
            Reset
          </Button>
        </Box>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by description, reference number, or category..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: colors.dark[200] }} /> }}
        />
      </Paper>

      {/* Double-Entry Cashbook Layout */}
      <Paper id="cashbook-content" sx={{ p: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight="bold">Cashbook Ledger (Double-Entry Format)</Typography>
            <Typography variant="body2" color={colors.dark[200]}>
              Period: {startDate} to {endDate}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">Show</Typography>
            <Select size="small" value={entriesPerPage} onChange={handleEntriesPerPageChange} sx={{ width: 70 }}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
            <Typography variant="body2">entries per side</Typography>
          </Box>
        </Box>

        {/* Two Column Layout - Income Left, Expense Right */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {/* LEFT SIDE - INCOME */}
          <Box sx={{ flex: 1, minWidth: '300px' }}>
            <Paper elevation={3} sx={{ 
              bgcolor: colors.green[50], 
              p: 1, 
              mb: 1,
              borderLeft: `4px solid ${colors.green[400]}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon sx={{ color: colors.green[400] }} />
                <Typography variant="h6" fontWeight="bold" color={colors.green[400]}>
                  INCOME
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Typography variant="subtitle2" color={colors.green[400]}>
                  Total: {formatCurrency(totals.income)}
                </Typography>
              </Box>
            </Paper>

            <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto', border: `1px solid ${colors.green[200]}` }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: colors.green[400] }}>
                    <TableCell sx={{ bgcolor: colors.green[400], color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ bgcolor: colors.green[400], color: 'white', fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ bgcolor: colors.green[400], color: 'white', fontWeight: 'bold' }}>Category</TableCell>
                    <TableCell sx={{ bgcolor: colors.green[400], color: 'white', fontWeight: 'bold', textAlign: 'right' }}>Amount (N$)</TableCell>
                    <TableCell sx={{ bgcolor: colors.green[400], color: 'white', fontWeight: 'bold' }}>Method</TableCell>
                    <TableCell sx={{ bgcolor: colors.green[400], color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentIncome.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                        <img src={Nodata} alt="No data" style={{ width: 80, marginBottom: 8 }} />
                        <Typography variant="body2">No income transactions</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    currentIncome.map((transaction) => (
                      <TableRow key={transaction.id} hover>
                        <TableCell>{transaction.transaction_date}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {transaction.description}
                          </Typography>
                          {transaction.reference_number && (
                            <Typography variant="caption" color={colors.dark[200]}>
                              Ref: {transaction.reference_number}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip label={transaction.category} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', color: colors.green[400] }}>
                          {formatCurrency(transaction.amount)}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={transaction.payment_method || 'Cash'} 
                            size="small" 
                            icon={<PaymentIcon />}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditDialog(transaction)}
                            sx={{ color: colors.blue[400] }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(transaction)}
                            sx={{ color: colors.red[400] }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* RIGHT SIDE - EXPENSE */}
          <Box sx={{ flex: 1, minWidth: '300px' }}>
            <Paper elevation={3} sx={{ 
              bgcolor: colors.red[50], 
              p: 1, 
              mb: 1,
              borderLeft: `4px solid ${colors.red[400]}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingDownIcon sx={{ color: colors.red[400] }} />
                <Typography variant="h6" fontWeight="bold" color={colors.red[400]}>
                  EXPENSES
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Typography variant="subtitle2" color={colors.red[400]}>
                  Total: {formatCurrency(totals.expense)}
                </Typography>
              </Box>
            </Paper>

            <TableContainer sx={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto', border: `1px solid ${colors.red[200]}` }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: colors.red[400] }}>
                    <TableCell sx={{ bgcolor: colors.red[400], color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ bgcolor: colors.red[400], color: 'white', fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ bgcolor: colors.red[400], color: 'white', fontWeight: 'bold' }}>Category</TableCell>
                    <TableCell sx={{ bgcolor: colors.red[400], color: 'white', fontWeight: 'bold', textAlign: 'right' }}>Amount (N$)</TableCell>
                    <TableCell sx={{ bgcolor: colors.red[400], color: 'white', fontWeight: 'bold' }}>Method</TableCell>
                    <TableCell sx={{ bgcolor: colors.red[400], color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentExpense.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                        <img src={Nodata} alt="No data" style={{ width: 80, marginBottom: 8 }} />
                        <Typography variant="body2">No expense transactions</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    currentExpense.map((transaction) => (
                      <TableRow key={transaction.id} hover>
                        <TableCell>{transaction.transaction_date}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {transaction.description}
                          </Typography>
                          {transaction.reference_number && (
                            <Typography variant="caption" color={colors.dark[200]}>
                              Ref: {transaction.reference_number}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip label={transaction.category} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', color: colors.red[400] }}>
                          {formatCurrency(transaction.amount)}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={transaction.payment_method || 'Cash'} 
                            size="small" 
                            icon={<PaymentIcon />}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditDialog(transaction)}
                            sx={{ color: colors.blue[400] }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(transaction)}
                            sx={{ color: colors.red[400] }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        {/* Pagination */}
        {filteredTransactions.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 2 }}>
            <IconButton
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              size="small"
              sx={{ border: `1px solid ${colors.dark[100]}` }}
            >
              <NavigateBeforeIcon />
            </IconButton>
            <Typography variant="body2">
              Page {currentPage} of {totalPages}
            </Typography>
            <IconButton
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              size="small"
              sx={{ border: `1px solid ${colors.dark[100]}` }}
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>
        )}
      </Paper>

      {/* Footer Summary */}
      <Paper sx={{ p: 2, mt: 2, bgcolor: colors.primary[50] }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon sx={{ color: colors.dark[200] }} />
            <Typography variant="body2" color={colors.dark[200]}>
              Total Transactions: {filteredTransactions.length}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box>
              <Typography variant="body2" color={colors.dark[200]}>Income</Typography>
              <Typography variant="h6" color={colors.green[400]} fontWeight="bold">
                {formatCurrency(totals.income)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color={colors.dark[200]}>Expenses</Typography>
              <Typography variant="h6" color={colors.red[400]} fontWeight="bold">
                {formatCurrency(totals.expense)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color={colors.dark[200]}>Net Balance</Typography>
              <Typography variant="h6" color={colors.blue[400]} fontWeight="bold">
                {formatCurrency(totals.balance)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {dialogMode === 'add' ? 'Add New Transaction' : 'Edit Transaction'}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Transaction Date"
              type="date"
              name="transaction_date"
              value={formData.transaction_date}
              onChange={handleFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleFormChange}
              fullWidth
              multiline
              rows={2}
              required
            />
            
            <FormControl fullWidth>
              <InputLabel>Transaction Type</InputLabel>
              <Select
                name="type"
                value={formData.type}
                onChange={handleFormChange}
                label="Transaction Type"
              >
                <MenuItem value="income">Income</MenuItem>
                <MenuItem value="expense">Expense</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                name="category"
                value={formData.category}
                onChange={handleFormChange}
                label="Category"
              >
                <MenuItem value="">Select Category</MenuItem>
                {(formData.type === 'income' ? incomeCategories : expenseCategories).map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label="Amount (N$)"
              name="amount"
              type="number"
              value={formData.amount}
              onChange={handleFormChange}
              fullWidth
              required
              InputProps={{ inputProps: { min: 0, step: 0.01 } }}
            />
            
            <FormControl fullWidth>
              <InputLabel>Payment Method</InputLabel>
              <Select
                name="payment_method"
                value={formData.payment_method}
                onChange={handleFormChange}
                label="Payment Method"
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                <MenuItem value="cheque">Cheque</MenuItem>
                <MenuItem value="mobile_money">Mobile Money</MenuItem>
                <MenuItem value="credit_card">Credit Card</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Reference Number"
              name="reference_number"
              value={formData.reference_number}
              onChange={handleFormChange}
              fullWidth
              placeholder="Invoice/Cheque/Receipt number"
            />
            
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleFormChange}
                label="Status"
              >
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            startIcon={<SaveIcon />}
            sx={{ bgcolor: colors.blue[400], '&:hover': { bgcolor: colors.blue[500] } }}
          >
            {dialogMode === 'add' ? 'Add Transaction' : 'Update Transaction'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this transaction?
            {transactionToDelete && (
              <Box sx={{ mt: 1, p: 1, bgcolor: colors.red[50], borderRadius: 1 }}>
                <Typography variant="body2" fontWeight="bold">{transactionToDelete.description}</Typography>
                <Typography variant="caption" color={colors.dark[200]}>
                  {transactionToDelete.transaction_date} - {formatCurrency(transactionToDelete.amount)}
                </Typography>
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" startIcon={<DeleteIcon />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CashbookView;